import { isValidElement, useEffect, useRef } from 'react';

import { BackgroundImage } from '../backgroundImage';
import { getSectionInnerWrapperAttributes, getSectionRootAttributes } from './getSectionElementRenderHTML';
import styles from './Section.css';
import type { SectionElementProps } from './Section.type';

function SectionInnerWrapper(props: SectionElementProps) {
  const { children } = props;
  return <div {...getSectionInnerWrapperAttributes(props)}>{children}</div>;
}

function showSection(element: HTMLElement | Element) {
  element.classList.add('visible');
}

export function SectionRoot(props: SectionElementProps) {
  const { element } = props;
  const { backgroundImage: elBackgroundImage, backgroundDarken, overlayColor, inAnimation } = element.attrs || {};

  let bgImage: any;
  if (elBackgroundImage) {
    bgImage = <BackgroundImage src={elBackgroundImage} darken={backgroundDarken} overlayColor={overlayColor} />;
  }

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return;
    if (!ref.current) return;

    // Section is in the viewport
    if (ref.current.getBoundingClientRect().y < window.innerHeight) {
      showSection(ref.current);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            showSection(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when at least 10% of the element is visible
        root: null, // viewport
        rootMargin: '0px',
      },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [inAnimation, ref.current]);

  return (
    <section {...getSectionRootAttributes(props)} ref={ref}>
      {isValidElement(bgImage) && (
        <div contentEditable={false} className={styles.backgroundImage}>
          {bgImage}
        </div>
      )}
      {props.children}
    </section>
  );
}

export function SectionElement(props: SectionElementProps) {
  const { children, ...restProps } = props;

  return (
    <SectionRoot {...restProps}>
      <SectionInnerWrapper {...restProps}>{children}</SectionInnerWrapper>
    </SectionRoot>
  );
}
